import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { DevstroupeConfirmationService } from '@devstroupe/services/confirmation';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { AllocationRole } from 'app/models/allocation.model';
import {
    CloseAllocationDialogComponent,
    CloseAllocationDialogData,
    CloseAllocationDialogResult,
} from '../close-allocation-dialog/close-allocation-dialog.component';
import { initialsFrom } from '../presence-day-hour-matrix/presence-day-hour-matrix.utils';

export interface ActualAllocationEditorUser {
    user_id: number;
    name: string;
    profile_type?: string | null;
}

export interface ActualAllocationEditorData {
    dateKey: string;
    timezone: string;
    role: string;
    roleLabel: string;
    shiftId: number | null;
    vehicleId: number | null;
    users: ActualAllocationEditorUser[];
    defaultUserId?: number | null;
    /** Horário previsto (schedule_entry) do usuário pré-selecionado, se houver — usado pelo botão "Sugerir horário previsto". */
    plannedStartedAtByUserId?: Record<number, string | Date>;
    /** Profissionais já escolhidos no dialog de escopo (várias funções). */
    presetUsers?: ActualAllocationEditorUser[];
    /** Tripulação completa da alocação existente. */
    crew?: ActualAllocationEditorUser[];
    clickedUserId?: number | null;
    /** Alocação existente (clique no retângulo). */
    existing?: {
        allocation_id: number;
        isOpen: boolean;
        label: string;
        started_at: string | Date;
        ended_at?: string | Date | null;
    } | null;
    /** Horário de fim previsto (schedule_entry/regime), se houver — botão "Usar horário da Escala" ao fechar. */
    suggestedEndedAt?: string | Date | null;
}

export type ActualAllocationEditorResult =
    | { mode: 'cancel' }
    | {
          mode: 'close';
          allocation_id: number;
          ended_at: string;
          scope: 'person' | 'all';
          user_id?: number;
      }
    | { mode: 'update'; allocation_id: number; started_at: string; ended_at?: string | null }
    | { mode: 'add-person' }
    | { mode: 'delete'; allocation_id: number }
    | {
          mode: 'save';
          users: { user_id: number; role: AllocationRole }[];
          started_at: string;
      };

export function profileToAllocationRole(profile?: string | null): AllocationRole {
    if (profile === 'DRIVER') return 'DRIVER';
    if (profile === 'DOCTOR') return 'DOCTOR';
    if (profile === 'NURSE' || profile === 'NURSING_TECH' || profile === 'PARAMEDIC') return 'NURSE';
    return 'OTHER';
}

@Component({
    selector: 'app-actual-allocation-editor-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        MatIconModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
    ],
    templateUrl: './actual-allocation-editor-dialog.component.html',
    styleUrls: ['./actual-allocation-editor-dialog.component.scss'],
})
export class ActualAllocationEditorDialogComponent {
    readonly initialsFrom = initialsFrom;
    selectedUserId: number | null;
    startedAtLocal: string;
    endedAtLocal: string | null = null;
    private readonly initialStartedAtLocal: string;
    private readonly initialEndedAtLocal: string | null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ActualAllocationEditorData,
        private dialogRef: MatDialogRef<ActualAllocationEditorDialogComponent, ActualAllocationEditorResult>,
        private dialog: MatDialog,
        private confirmationService: DevstroupeConfirmationService,
    ) {
        if (data.existing) {
            this.selectedUserId = data.clickedUserId ?? null;
            const started = DateTime.fromJSDate(new Date(data.existing.started_at), { zone: data.timezone });
            this.startedAtLocal = started.toFormat("yyyy-MM-dd'T'HH:mm");
            this.initialStartedAtLocal = this.startedAtLocal;
            if (data.existing.ended_at) {
                const ended = DateTime.fromJSDate(new Date(data.existing.ended_at), { zone: data.timezone });
                this.endedAtLocal = ended.toFormat("yyyy-MM-dd'T'HH:mm");
            }
            this.initialEndedAtLocal = this.endedAtLocal;
            return;
        }
        this.selectedUserId = data.defaultUserId ?? data.users[0]?.user_id ?? null;
        this.startedAtLocal = DateTime.now().setZone(data.timezone).toFormat("yyyy-MM-dd'T'HH:mm");
        this.initialStartedAtLocal = this.startedAtLocal;
        this.initialEndedAtLocal = null;
    }

    get isExisting(): boolean {
        return !!this.data.existing;
    }

    get isClosed(): boolean {
        return !!this.data.existing && !this.data.existing.isOpen;
    }

    get isCrewStart(): boolean {
        return !this.data.existing && (this.data.presetUsers?.length ?? 0) > 0;
    }

    get hasScheduleChanges(): boolean {
        return this.startedAtLocal !== this.initialStartedAtLocal || this.endedAtLocal !== this.initialEndedAtLocal;
    }

    get filteredUsers() {
        const role = this.data.role;
        return (this.data.users || []).filter((u) => !role || (u.profile_type || 'OTHER') === role);
    }

    get crewNames(): string {
        return (this.data.crew || [])
            .map((u) => u.name)
            .filter(Boolean)
            .join(', ');
    }

    get canSaveStart(): boolean {
        if (!this.startedAtLocal) return false;
        if (this.isCrewStart) return (this.data.presetUsers?.length ?? 0) > 0;
        return !!this.selectedUserId;
    }

    /** Horário previsto (escala) do profissional selecionado, se conhecido. */
    get plannedStartLabel(): string | null {
        if (this.isCrewStart || !this.selectedUserId) return null;
        const planned = this.data.plannedStartedAtByUserId?.[this.selectedUserId];
        if (!planned) return null;
        return DateTime.fromJSDate(new Date(planned), { zone: this.data.timezone }).toFormat('HH:mm');
    }

    /** Duração da alocação: início → agora (aberta) ou início → fim (fechada). */
    get durationLabel(): string | null {
        if (!this.data.existing) return null;
        const start = DateTime.fromISO(this.startedAtLocal);
        const end = this.data.existing.isOpen ? DateTime.now() : DateTime.fromISO(this.endedAtLocal || this.startedAtLocal);
        if (!start.isValid || !end.isValid) return null;
        const minutes = Math.max(0, Math.floor(end.diff(start, 'minutes').minutes));
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min`;
    }

    suggestPlannedStart(): void {
        if (!this.selectedUserId) return;
        const planned = this.data.plannedStartedAtByUserId?.[this.selectedUserId];
        if (!planned) return;
        this.startedAtLocal = DateTime.fromJSDate(new Date(planned), { zone: this.data.timezone }).toFormat(
            "yyyy-MM-dd'T'HH:mm",
        );
    }

    /** Horário de fim previsto (escala), se conhecido — usado no botão "Usar horário da Escala" ao fechar. */
    get suggestedEndLabel(): string | null {
        if (!this.data.suggestedEndedAt) return null;
        return DateTime.fromJSDate(new Date(this.data.suggestedEndedAt), { zone: this.data.timezone }).toFormat('HH:mm');
    }

    suggestPlannedEnd(): void {
        if (!this.data.suggestedEndedAt) return;
        this.endedAtLocal = DateTime.fromJSDate(new Date(this.data.suggestedEndedAt), { zone: this.data.timezone }).toFormat(
            "yyyy-MM-dd'T'HH:mm",
        );
    }

    cancel(): void {
        this.dialogRef.close({ mode: 'cancel' });
    }

    save(): void {
        const people = this.isCrewStart
            ? this.data.presetUsers || []
            : this.filteredUsers.filter((u) => u.user_id === this.selectedUserId);
        if (!people.length || !this.startedAtLocal) return;
        const dt = DateTime.fromISO(this.startedAtLocal, { zone: this.data.timezone });
        this.dialogRef.close({
            mode: 'save',
            users: people.map((u) => ({
                user_id: u.user_id,
                role: profileToAllocationRole(u.profile_type || this.data.role),
            })),
            started_at: dt.toUTC().toISO()!,
        });
    }

    saveSchedule(): void {
        if (!this.data.existing?.allocation_id || !this.startedAtLocal) return;
        const startDt = DateTime.fromISO(this.startedAtLocal, { zone: this.data.timezone });
        const endDt = this.endedAtLocal ? DateTime.fromISO(this.endedAtLocal, { zone: this.data.timezone }) : null;
        this.dialogRef.close({
            mode: 'update',
            allocation_id: this.data.existing.allocation_id,
            started_at: startDt.toUTC().toISO()!,
            ended_at: endDt ? endDt.toUTC().toISO()! : this.data.existing.ended_at ? undefined : null,
        });
    }

    closeAllocation(): void {
        if (!this.data.existing?.allocation_id) return;
        const crew = this.data.crew || [];
        const drivers = crew.filter((u) => (u.profile_type || '') === 'DRIVER');
        const clicked = crew.find((u) => u.user_id === this.data.clickedUserId);
        const clickedIsDriver = (clicked?.profile_type || this.data.role) === 'DRIVER';
        const isOnlyDriver = this.data.vehicleId != null && clickedIsDriver && drivers.length <= 1;
        const dialogData: CloseAllocationDialogData = {
            timezone: this.data.timezone,
            label: this.data.existing.label,
            crewCount: crew.length || 1,
            isOnlyDriver,
        };
        const ref = this.dialog.open(CloseAllocationDialogComponent, {
            width: '380px',
            data: dialogData,
        });
        ref.afterClosed().subscribe((result: CloseAllocationDialogResult | undefined) => {
            if (!result || result.mode !== 'confirm' || !this.data.existing?.allocation_id) return;
            this.dialogRef.close({
                mode: 'close',
                allocation_id: this.data.existing.allocation_id,
                ended_at: result.ended_at,
                scope: result.scope,
                user_id: this.data.clickedUserId ?? undefined,
            });
        });
    }

    addPerson(): void {
        this.dialogRef.close({ mode: 'add-person' });
    }

    async remove(): Promise<void> {
        if (!this.data.existing?.allocation_id) return;
        const confirmed = await firstValueFrom(
            this.confirmationService
                .open({
                    title: 'Remover turno',
                    message: `Remover o turno de ${this.data.existing.label}? Essa ação não pode ser desfeita.`,
                    icon: { show: true, name: 'heroicons_outline:exclamation-triangle', color: 'warn' },
                    actions: { confirm: { show: true, label: 'Remover' }, cancel: { show: true, label: 'Cancelar' } },
                    dismissible: true,
                })
                .afterClosed(),
        );
        if (!confirmed) return;
        this.dialogRef.close({ mode: 'delete', allocation_id: this.data.existing.allocation_id });
    }
}
